import type { ActivityRecord } from '@/types/activity';

export interface ActivityText {
  /** What happened, in words a person would say: "Changed hq's config". */
  title: string;
  /** The specifics, when the record carries any. Empty otherwise. */
  detail: string;
  /** Where to go to see the thing the entry is about. */
  link?: string;
  /** Who did it: the part of the email before the @. */
  who: string;
  failed: boolean;
}

const sentence = (text: string) => {
  const spaced = text.replace(/[-_]+/g, ' ').trim();
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : '';
};

const credits = (n: number) => `${n.toLocaleString()} ${Math.abs(n) === 1 ? 'credit' : 'credits'}`;

const firstChanges = (changes?: string[]) => {
  if (!changes || changes.length === 0) return '';
  const shown = changes.slice(0, 2).join(', ');
  const more = changes.length - 2;
  return more > 0 ? `${shown} and ${more} more` : shown;
};

/**
 * Turn one activity record into the sentence the Activity page and Home show.
 * The record stores a raw action name ("billing-admin-grant"); this is the one
 * place that knows what each action means to a person.
 */
export function describeActivity(activity: ActivityRecord): ActivityText {
  const id = activity.resourceId;
  // Callers log what they have: some put the facts under `metadata`, some beside it.
  const details = activity.details || {};
  const meta = details.metadata || {};
  const who = (activity.userEmail || activity.userId || '').split('@')[0];
  const client = `?client=${encodeURIComponent(id)}`;
  let title = '';
  let detail = '';
  let link: string | undefined;

  if (activity.resourceType === 'client-credits') {
    link = `/numa-credits${client}`;
    if (activity.action === 'topup') {
      title =
        typeof details.credits === 'number'
          ? `Added ${credits(details.credits)} for ${id}`
          : `Added a credit top-up for ${id}`;
    } else if (activity.action === 'billing-admin-grant') {
      title = meta.email ? `Gave ${meta.email} billing access on ${id}` : `Gave someone billing access on ${id}`;
    } else if (activity.action === 'billing-admin-revoke') {
      title = meta.email
        ? `Removed ${meta.email}'s billing access on ${id}`
        : `Removed a person's billing access on ${id}`;
    } else if (typeof details.adjustment === 'number') {
      title = `Adjusted ${id}'s top-up balance by ${credits(details.adjustment)}`;
      detail = details.note || '';
    } else if (typeof details.showCredits === 'boolean') {
      title = details.showCredits ? `Showed credits in ${id}'s app` : `Hid credits in ${id}'s app`;
    } else {
      title = `Changed credit settings for ${id}`;
      detail = firstChanges(details.changes);
    }
  } else if (activity.resourceType === 'password-reset') {
    link = '/tools/reset-all-passwords';
    title = `Reset everyone's password in ${id}`;
    detail = typeof meta.total === 'number' ? `${meta.total} people` : '';
  } else if (activity.resourceType === 'fixed-ip') {
    link = '/tools/fixed-ip-fleet';
    if (meta.action === 'pause') {
      title = `Paused the fixed IP for ${id}`;
      detail = [meta.hours ? `for ${meta.hours} h` : '', meta.reason || ''].filter(Boolean).join(': ');
    } else if (meta.action === 'resume') {
      title = `Resumed the fixed IP for ${id}`;
    } else {
      title = `Changed the fixed IP for ${id}`;
    }
  } else if (activity.resourceType === 'aws-account') {
    link = '/configs';
    title =
      activity.action === 'account-rename' && meta.newName
        ? `Renamed AWS account ${id} to ${meta.newName}`
        : `${sentence(activity.action)}: AWS account ${id}`;
  } else if (activity.resourceType === 'support-docs') {
    link = '/tools/support-docs-manager';
    title = `Sent the support docs to ${id}`;
  } else if (activity.resourceType === 'announcement') {
    link = '/tools/announcements';
    const verbs: Record<string, string> = {
      posted: `Posted an announcement to ${id}`,
      edited: `Edited an announcement for ${id}`,
      revoked: `Withdrew an announcement from ${id}`,
      restored: `Restored an announcement for ${id}`,
    };
    title = verbs[activity.action] || `${sentence(activity.action)} an announcement for ${id}`;
    detail = meta.title || '';
  } else if (activity.resourceType === 'client-flags') {
    // The two tags a person puts on a client: do not deploy, and inactive.
    link = `/configs${client}`;
    const reason = typeof meta.reason === 'string' ? meta.reason.trim() : '';
    switch (activity.action) {
      case 'deploy-block-set':
        title = reason ? `Blocked deploys to ${id}: ${reason}` : `Blocked deploys to ${id}`;
        break;
      case 'deploy-block-lifted':
        title = `Allowed deploys to ${id} again`;
        detail = reason ? `The block said: ${reason}` : '';
        break;
      case 'inactive-set':
        title = `Marked ${id} inactive`;
        detail = reason;
        break;
      case 'inactive-lifted':
        title = `Marked ${id} active again`;
        break;
      case 'deploy-block-overridden':
        title = `Deployed to ${id} despite a do-not-deploy block`;
        detail = reason ? `The block says: ${reason}` : '';
        if (meta.deploymentId) link = `/deployments/${meta.deploymentId}/logs`;
        break;
      default:
        title = `${sentence(activity.action)}: ${id}`;
    }
  } else if (activity.type === 'config') {
    link = `/configs${client}`;
    switch (activity.action) {
      case 'created':
        title = `Created the config for ${id}`;
        break;
      case 'deleted':
        title = `Deleted the config for ${id}`;
        link = '/configs';
        break;
      case 'updated':
        title = `Changed ${id}'s config`;
        detail = firstChanges(details.changes);
        break;
      default:
        title = `${sentence(activity.action)}: ${id}`;
    }
  } else if (activity.type === 'user') {
    link = '/users';
    switch (activity.action) {
      case 'created':
        title = `Added portal user ${id}`;
        break;
      case 'deleted':
        title = `Removed portal user ${id}`;
        break;
      case 'password-reset':
        title = `Reset the password for ${id}`;
        break;
      default:
        title = `${sentence(activity.action)}: ${id}`;
    }
  } else if (activity.type === 'deployment') {
    link = meta.deploymentId ? `/deployments/${meta.deploymentId}/logs` : `/deployments${client}`;
    title = activity.action === 'started' ? `Started a deploy to ${id}` : `Deployed to ${id}`;
    detail = meta.imageTag ? `Image ${meta.imageTag}` : '';
  } else {
    title = meta.title || `${sentence(activity.action)}: ${id}`;
  }

  if (!activity.success && activity.errorMessage) {
    detail = detail ? `${detail}. ${activity.errorMessage}` : activity.errorMessage;
  }

  return { title, detail, link, who, failed: !activity.success };
}
